import React from 'react';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import ActionMenu, { ActionMenuItem } from '@components/RingilComponents/ActionMenu.tsx';
import NavigationBreadCrumb from '@components/ringil3/BreadCrumb/NavigationBreadCrumb.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgArrowUp from '@icons/arrow-up.svg?react';
import SvgPlus from '@icons/plus-thick.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import { OrdersConfig } from '@components/framework/orders/validation.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Form } from '@hooks/useForm/useForm.tsx';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { getRouteTestIds } from '@app/pages/expedition/create/testIds.ts';
import { RouteOrderIssue } from '@app/pages/expedition/create/routeRules.ts';
import LocationOrdersPanel from '@app/pages/expedition/create/components/LocationOrdersPanel.tsx';
import LineStopCard from '@app/pages/customer/LineEdit/components/LineStopCard.tsx';
import { LineForm, LineStop } from '@app/pages/customer/LineEdit/types.ts';

interface ComponentProps {
  form: Form<LineForm>;
  stop: LineStop;
  locIndex: number;
  ordersConfig: OrdersConfig;
  companyId: string;
  canDeleteLocation: boolean;
  onDeleteLocation: (locIndex: number) => void;
  onMoveStop: (fromIndex: number, toIndex: number) => void;
  onAddStopAbove: (index: number) => void;
  onDeleteUnloading: (locIndex: number) => void;
  unloadableOrders: GlobalOrdersRequest[];
  onUnloadOrder: (locIndex: number, orderId: string) => void;
  onOrderAdded: (locIndex: number, orderId: string) => void;
  onMoveOrderLoading: (orderId: string, targetLocIndex: number) => void;
  orderIssues: RouteOrderIssue[];
}

/**
 * One stop of a Linka: the same frame as the expedition RouteStop (breadcrumb, stop actions, cargo on
 * the right through the shared LocationOrdersPanel), with the Linka location card on the left.
 */
const LineRouteStop: React.FC<ComponentProps> = ({
  form,
  stop,
  locIndex,
  ordersConfig,
  companyId,
  canDeleteLocation,
  onDeleteLocation,
  onMoveStop,
  onAddStopAbove,
  onDeleteUnloading,
  unloadableOrders,
  onUnloadOrder,
  onOrderAdded,
  onMoveOrderLoading,
  orderIssues,
}) => {
  const { tr } = useTranslationLgs();
  const locationCount = form.values.locations.length;
  const testIds = getRouteTestIds(locIndex, locationCount);
  const isLast = locIndex === locationCount - 1;

  const stopName = (s: LineStop) => {
    const names = (s.candidates ?? []).filter(c => c.locationId).map(c => c.name ?? '');
    if (names.length === 0) return tr('LineEdit.anyLocation', 'Libovolná lokalita');
    return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
  };

  const stopActions: ActionMenuItem[] = [
    {
      key: 'moveUp',
      label: tr('CreateExpedition.moveStopUp', 'Move up'),
      icon: <SvgArrowUp />,
      disabled: locIndex === 0,
      onClick: () => onMoveStop(locIndex, locIndex - 1),
    },
    {
      key: 'moveDown',
      label: tr('CreateExpedition.moveStopDown', 'Move down'),
      icon: <SvgArrowUp className={'rotate-180'} />,
      disabled: isLast,
      onClick: () => onMoveStop(locIndex, locIndex + 1),
    },
    {
      key: 'addAbove',
      label: tr('LineEdit.addStopAbove', 'Mezizastávka'),
      icon: <SvgPlus />,
      onClick: () => onAddStopAbove(locIndex),
    },
    {
      key: 'delete',
      label: tr('CreateExpedition.deleteStop', 'Delete stop'),
      icon: <SvgDelete />,
      danger: true,
      disabled: !canDeleteLocation,
      onClick: () => onDeleteLocation(locIndex),
    },
  ];

  const updateStop = (patch: Partial<LineStop>) =>
    form.useProduce(draft => {
      Object.assign(draft.locations[locIndex], patch);
    });

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex flex-row items-center justify-between'}>
        <NavigationBreadCrumb
          locationCount={locationCount}
          currentPosition={locIndex}
          idPrefix={'line'}
          locations={form.values.locations.map((s, idx) => ({ id: s.id ?? String(idx), name: stopName(s) }))}
        />
        <ActionMenu
          icon={<SvgPencil />}
          tooltip={tr('CreateExpedition.stopActionsTooltip', 'Stop actions')}
          heading={tr('CreateExpedition.stopActionsHeading', 'Stop actions')}
          items={stopActions}
        />
      </div>

      <div className={'grid grid-cols-1 gap-4 lg:grid-cols-2'}>
        <LineStopCard stop={stop} locIndex={locIndex} onChange={updateStop} validationErrors={form.validationErrors} dataTestIdPrefix={testIds.locationSelect} />
        <LocationOrdersPanel
          // The Linka form is a superset of the expedition form for everything the panel touches
          // (locations[].orders / unloadingOrderIds, distribution = null).
          form={form as unknown as Form<NewExpeditionForm>}
          locIndex={locIndex}
          location={stop}
          locationCount={locationCount}
          ordersConfig={ordersConfig}
          companyId={companyId}
          testIds={testIds}
          onDeleteUnloading={onDeleteUnloading}
          unloadableOrders={unloadableOrders}
          onUnloadOrder={onUnloadOrder}
          onOrderAdded={onOrderAdded}
          onMoveOrderLoading={onMoveOrderLoading}
          orderIssues={orderIssues}
        />
      </div>
    </div>
  );
};

export default LineRouteStop;
